import { API_METHODS } from '@compass/client/constants/api-methods';
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GenericTable } from '@/components/GenericTable';
import { Badge } from '@/components/ui/badge';
import { useAPIPermission } from '@/hooks/usePermissions';
import type { AgentListRow, AgentsSortBy, AgentsSortOrder } from '@/pages/analytics/types';
import { buildAnalyticsAgentDetailPath } from '@/pages/analytics/utils/resolveOrg';
import {
  formatAggregateScore,
  SCORE_BAND_EXEMPLARY_MIN,
  SCORE_BAND_MEETS_MIN,
} from '@/pages/qc/utils';
import type { SortState, TableColumnConfig, TableConfig } from '@/types/table';
import { cn } from '@/utils';

import { LastFiveDots } from './LastFiveDots';

interface AgentsTableProps {
  organizationId: string;
  rows: AgentListRow[];
  loading?: boolean;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  sortBy: AgentsSortBy;
  sortOrder: AgentsSortOrder;
  onSortChange: (next: { sortBy: AgentsSortBy; sortOrder: AgentsSortOrder }) => void;
}

const scoreBadgeClass = (score: number): string => {
  if (score >= SCORE_BAND_EXEMPLARY_MIN) return 'bg-positive text-positive-foreground';
  if (score >= SCORE_BAND_MEETS_MIN) return 'bg-notice text-notice-foreground';
  return 'bg-attention text-attention-foreground';
};

const AGENT_COLUMN_WIDTH = 360;
const SCORE_COLUMN_WIDTH = 180;
const LAST_CALLS_COLUMN_WIDTH = 180;
const CALL_COUNT_COLUMN_WIDTH = 140;

export const AgentsTable: React.FC<AgentsTableProps> = ({
  organizationId,
  rows,
  loading,
  currentPage,
  pageSize,
  hasNextPage,
  onPageChange,
  onPageSizeChange,
  sortBy,
  sortOrder,
  onSortChange,
}) => {
  const [searchParams] = useSearchParams();
  // The agent-detail drilldown reads per-agent evaluations, so it's gated on
  // evaluation read. Analytics-only users (qc-analytics-viewer) can see this
  // table but must not get a clickable row into a page they'd be redirected out
  // of; when they lack the permission we drop the row link (and its cursor).
  const { hasPermission: canViewAgentDetail } = useAPIPermission(
    API_METHODS.EvaluationService.ListEvaluations,
  );
  const columns: TableColumnConfig<AgentListRow>[] = useMemo(
    () => [
      {
        key: 'agent_name',
        header: 'Agent',
        width: AGENT_COLUMN_WIDTH,
        accessor: item => (
          <span className="block truncate font-medium text-foreground">{item.agent_name}</span>
        ),
      },
      {
        key: 'avg_score',
        header: 'Average Score',
        width: SCORE_COLUMN_WIDTH,
        sortable: true,
        sortField: 'avg_score',
        accessor: item => (
          <Badge
            variant="outline"
            className={cn('border-transparent', scoreBadgeClass(item.avg_score))}
          >
            {formatAggregateScore(item.avg_score)}
          </Badge>
        ),
      },
      {
        key: 'last_calls',
        header: 'Last 5 Calls',
        width: LAST_CALLS_COLUMN_WIDTH,
        accessor: item => <LastFiveDots scores={item.last_calls.map(c => c.score)} />,
      },
      {
        key: 'call_count',
        header: 'Call Count',
        width: CALL_COUNT_COLUMN_WIDTH,
        sortable: true,
        sortField: 'call_count',
        accessor: item => <span className="tabular-nums">{item.call_count}</span>,
      },
    ],
    [],
  );

  const config: TableConfig<AgentListRow> = useMemo(
    () => ({
      columns,
      getRowLink: canViewAgentDetail
        ? item => {
            const params = new URLSearchParams(searchParams);
            params.set('tab', 'agents');
            // The Agents tab has no direction filter; don't leak the Org tab's into the detail page.
            params.delete('call_directions');
            const query = params.toString();
            const detailPath = buildAnalyticsAgentDetailPath(organizationId, item.agent_id);
            return `${detailPath}${query ? `?${query}` : ''}`;
          }
        : undefined,
      // Without a drilldown, rows are inert — drop the pointer cursor that
      // GenericTable's default row styling would otherwise imply.
      getRowClassName: canViewAgentDetail
        ? undefined
        : () => 'group hover:bg-muted transition-colors duration-200',
      getRowKey: item => item.agent_id,
      emptyState: {
        title: 'No agents found',
        description: 'There are no agents matching your current filters.',
      },
    }),
    [columns, organizationId, searchParams, canViewAgentDetail],
  );

  const sortState: SortState[] = useMemo(
    () => [{ field: sortBy, direction: sortOrder }],
    [sortBy, sortOrder],
  );

  // Only the two columns configured with `sortable: true` can produce a
  // SortState — both with `sortField` values inside AgentsSortBy. The guard
  // is defensive: any future drift in column config falls back to the
  // default sort instead of letting an invalid value escape to the API.
  const isValidSortBy = (field: string): field is AgentsSortBy =>
    field === 'avg_score' || field === 'call_count';

  const handleSortChange = (next: SortState[]) => {
    // GenericTable appends newly clicked fields; use the newest valid sort.
    const nextSort = [...next]
      .reverse()
      .find((sort): sort is SortState & { field: AgentsSortBy } => isValidSortBy(sort.field));

    if (!nextSort) {
      onSortChange({ sortBy, sortOrder: sortOrder === 'asc' ? 'desc' : 'asc' });
      return;
    }

    onSortChange({ sortBy: nextSort.field, sortOrder: nextSort.direction });
  };

  return (
    <GenericTable<AgentListRow>
      config={config}
      data={rows}
      loading={loading}
      error={null}
      currentPage={currentPage}
      hasNextPage={hasNextPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      pageSizeOptions={[20, 50, 100]}
      onPageSizeChange={onPageSizeChange}
      sortState={sortState}
      onSortChange={handleSortChange}
    />
  );
};
